import React from "react";
import { motion } from "framer-motion";

import s2 from "../../accests/galle/galle.jpeg"
import s3 from "../../accests/galle/koggala.jpeg"
import s4 from "../../accests/yala/yaala (4).jpeg"
import s1 from "../../accests/galle/tur.jpg"
import s5 from "../../accests/udawalawa/u2.jpeg"

const day1Highlights = [
    {
        title: "Kosgoda Turtle Hatchery",
        desc: "Begin your journey with a visit to the Kosgoda Turtle Hatchery, a conservation center dedicated to protecting endangered sea turtles. Learn about turtle nesting, egg incubation, and the efforts taken to release hatchlings safely into the ocean.",
        img: s1,
        wiki: "https://en.wikipedia.org/wiki/Kosgoda",
    },
    {
        title: "Galle Dutch Fort",
        desc: "Explore the historic Galle Dutch Fort, a UNESCO World Heritage Site showcasing centuries of colonial influence. Walk through charming cobblestone streets, admire colonial-era buildings, and enjoy breathtaking views of the Indian Ocean.",
        img: s2,
        wiki: "https://en.wikipedia.org/wiki/Galle_Fort",
    },
    {
        title: "Traditional Stilt Fishing",
        desc: "Witness the iconic tradition of stilt fishing along Sri Lanka’s southern coast. This centuries-old fishing method offers a unique glimpse into local culture and provides excellent photo opportunities.",
        img: s3,
        wiki: "https://en.wikipedia.org/wiki/Stilt_fishing",
    },
];

const day2Highlights = [
    {
        title: "Yala National Park – Jeep Safari",
        desc: "Embark on an exhilarating jeep safari in Yala National Park, renowned for having one of the highest leopard densities in the world. Spot elephants, sloth bears, crocodiles, and a wide variety of bird species across diverse ecosystems.",
        img: s4,
        wiki: "https://en.wikipedia.org/wiki/Yala_National_Park",
    },
    {
        title: "Udawalawe Elephant Transit Home",
        desc: "Visit the Udawalawe Elephant Transit Home, where orphaned and injured elephants are rehabilitated before being released back into the wild. Observe feeding sessions and learn about Sri Lanka’s elephant conservation initiatives.",
        img: s5,
        wiki: "https://en.wikipedia.org/wiki/Elephant_Transit_Home",
    },
];

const info = [
    { label: "Trip Duration", value: "02 Days" },
    { label: "Language", value: "English" },
    { label: "Best Time to Travel", value: "Year-round" },
    { label: "Transfers", value: "Hotel – Attractions – Hotel" },
];

const inclusions = [
    "Private air-conditioned transportation (Car, Van, Mini Coach, or Luxury Bus)",
    "Comfortable overnight accommodation with selected meal plan",
    "Professional English speaking chauffeur guide",
    "Jeep safari charges and entrance fees for all listed attractions",
    "All sightseeing and tour-related operational costs",
    "Unlimited complimentary bottled mineral water",
    "All applicable government taxes and service charges",
];

const exclusions = [
    "Meals and beverages not included in the stated meal plan",
    "International or domestic flights and visa arrangements",
    "Personal expenses such as tips, laundry, or optional activities",
];

const travelTips = [
    "Clothing: Wear light, breathable clothing suitable for warm weather and safari conditions.",
    "Sun Protection: Sunscreen, hats, and sunglasses are strongly recommended.",
    "Footwear: Comfortable walking shoes are ideal for fort exploration and short walks.",
    "Wildlife Etiquette: Maintain silence during safaris to maximize wildlife sightings.",
    "Photography: Bring a camera with zoom lens for wildlife and scenic photography.",
];

export default function YalaSafariGalleOvernightTwoDayTour() {
    return (
        <div className="overflow-x-hidden">

            {/* Hero Section */}
            <section className="relative h-[70vh] w-full overflow-hidden lg:mt-20">
                <img
                    src={s4}
                    alt="Yala Safari & Galle Overnight Tour"
                    className="w-full h-full object-cover"
                />
                <div
                    className="absolute inset-0 bg-gradient-to-b from-black/60 to-black/40 flex flex-col items-center justify-center text-center px-4">
                    <h1 className="text-4xl md:text-5xl text-white font-bold mb-3">
                        Yala Safari & Galle Overnight Tour
                    </h1>
                    <p className="text-lg md:text-xl text-white/90 max-w-3xl">
                        A perfect blend of heritage, wildlife encounters, and scenic coastal beauty
                    </p>
                </div>
            </section>

            <div className="max-w-7xl mx-auto px-4 py-16 space-y-16">

            {/* Day 1 */}
            <section className="space-y-12">
                <h2 className="text-3xl font-bold text-center">
                    Day 1 – Galle & Southern Coast Exploration
                </h2>

                {day1Highlights.map((item, idx) => (
                    <motion.div
                        key={idx}
                        initial={{opacity: 0, x: idx % 2 === 0 ? -50 : 50}}
                        whileInView={{opacity: 1, x: 0}}
                        viewport={{once: true}}
                        className={`md:flex md:items-center md:space-x-8 bg-white shadow-lg rounded-xl overflow-hidden ${
                            idx % 2 === 1 ? "md:flex-row-reverse" : ""
                        }`}
                    >
                        <img
                            src={item.img}
                            alt={item.title}
                            className="md:w-1/2 h-64 object-cover"
                        />
                        <div className="p-6 md:w-1/2">
                            <h3 className="text-2xl font-bold mb-4">{item.title}</h3>
                            <p className="text-gray-700">{item.desc}</p>
                            <a
                                href={item.wiki}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-block mt-4 text-blue-600 font-semibold hover:text-blue-800"
                            >
                                Read More →
                            </a>
                        </div>
                    </motion.div>
                ))}
            </section>

            {/* Day 2 */}
            <section className="space-y-12">
                <h2 className="text-3xl font-bold text-center">
                    Day 2 – Wildlife Safari Experience
                </h2>

                {day2Highlights.map((item, idx) => (
                    <motion.div
                        key={idx}
                        initial={{opacity: 0, x: idx % 2 === 0 ? -50 : 50}}
                        whileInView={{opacity: 1, x: 0}}
                        viewport={{once: true}}
                        className={`md:flex md:items-center md:space-x-8 bg-white shadow-lg rounded-xl overflow-hidden ${
                            idx % 2 === 1 ? "md:flex-row-reverse" : ""
                        }`}
                    >
                        <img
                            src={item.img}
                            alt={item.title}
                            className="md:w-1/2 h-64 object-cover"
                        />
                        <div className="p-6 md:w-1/2">
                            <h3 className="text-2xl font-bold mb-4">{item.title}</h3>
                            <p className="text-gray-700">{item.desc}</p>
                            <a
                                href={item.wiki}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-block mt-4 text-blue-600 font-semibold hover:text-blue-800"
                            >
                                Read More →
                            </a>
                        </div>
                    </motion.div>
                ))}
            </section>

            {/* Info */}
            <section
                className="bg-yellow-50 p-8 rounded-xl shadow-lg grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {info.map((item, idx) => (
                    <div key={idx} className="text-center">
                        <h4 className="text-lg font-semibold">{item.label}</h4>
                        <p className="text-gray-700">{item.value}</p>
                    </div>
                ))}
            </section>

            {/* Inclusions & Exclusions */}
            <section data-aos="fade-up"
                     data-aos-duration="3000" className="grid md:grid-cols-2 gap-8">
                <div className="bg-white p-6 rounded-xl shadow-lg">
                    <h3 className="text-2xl font-bold mb-4">Tour Inclusions</h3>
                    <ul className="list-disc list-inside space-y-2 text-gray-700">
                        {inclusions.map((i, idx) => <li key={idx}>{i}</li>)}
                    </ul>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-lg">
                    <h3 className="text-2xl font-bold mb-4">Tour Exclusions</h3>
                    <ul className="list-disc list-inside space-y-2 text-gray-700">
                        {exclusions.map((e, idx) => <li key={idx}>{e}</li>)}
                    </ul>
                </div>
            </section>


            {/* Travel Tips */}
            <section data-aos="fade-up"
                     data-aos-duration="3000" className="bg-gray-50 p-6 rounded-xl shadow-lg">
                <h3 className="text-2xl font-bold mb-4">Travel Tips</h3>
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                    {travelTips.map((tip, idx) => <li key={idx}>{tip}</li>)}
                </ul>
            </section>

            {/* WhatsApp CTA */}
            <motion.div
                initial={{opacity: 0, y: 40}}
                whileInView={{opacity: 1, y: 0}}
                transition={{duration: 0.8}}
                className="mt-14 text-center"
            >
                <motion.a
                    whileHover={{scale: 1.08}}
                    whileTap={{scale: 0.95}}
                    href="https://wa.me/94770163670?text=Hello%20I%20would%20like%20to%20book%20the%20Yala%20Safari%20%26%20Galle%20Overnight%20Two%20Day%20Tour.%20Please%20share%20availability%20and%20pricing.%20Thank%20you."
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center
        px-14 py-4 bg-green-600 text-white
        font-semibold text-lg rounded-full
        shadow-xl hover:bg-green-500
        transition-all duration-300"
                >
                    Book This Tour
                </motion.a>

                <p className="mt-4 text-sm text-gray-600">
                    💬 Instant WhatsApp Support • 🐆 Safari Experts • 🚙 Comfortable Transport
                </p>
            </motion.div>

            </div>
        </div>
    );
}
